import { Button } from '@base-ui/react/button';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ParseKeys } from 'i18next';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import buttonStyles from '@/admin/styles/button.module.css';
import { HelperFilter } from '@/admin/ui/HelperFilter';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import {
  isEmptyFilter,
  readTableSearch,
  type TableFilters,
  type TableSearchState,
  type TableSortState,
  writeTableSearch,
} from '@/admin/ui/tableSearch';
import api from '@/api';
import {
  type ListFilterOverride,
  listFiltersToFilterFields,
} from '@/tables/listFilterFields';
import TableFilterPanel from '@/tables/TableFilterPanel';
import type { FileRoutesByFullPath } from '~/routeTree.gen';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';

// Keystrokes settle for this long before they become a request.
const SEARCH_DEBOUNCE_MS = 300;

/** What a list endpoint needs beyond the page index and the search text. */
export type TableExtras = {
  paging: { limit: number; offset: number };
  sort?: TableSortState;
  filters: TableFilters;
};

type UseDataResult<D> = {
  data?: {
    data: D[];
    count?: number;
  };
  isLoading: boolean;
  error: unknown;
};

type TablePageSettings<D, P extends keyof FileRoutesByFullPath> = {
  /** An `admin.*` translation key for the page title. */
  name: ParseKeys;
  /**
   * `page` is 0-based. `extras` is the same window as `{limit, offset}` plus
   * the sort and the applied filters; a consumer that ignores it behaves as
   * before.
   */
  useData: (
    page: number,
    search: string,
    extras: TableExtras,
  ) => UseDataResult<D>;
  columns: ColumnDef<D, any>[];
  /** With `getParams`, makes rows clickable; leave both out for a plain list. */
  itemBaseUrl?: P;
  getParams?: (d: D) => FileRoutesByFullPath[P]['types']['allParams'];
  // When set, renders a search box whose value is passed to useData as the
  // second argument (and resets paging to the first page on change).
  searchable?: boolean;
  /** An `admin.*` translation key. */
  searchPlaceholder?: ParseKeys;
  // Optional header content rendered to the right of the title, e.g. a
  // "Create" button.
  actions?: ReactNode;
  /** `GET /v3/lists/{listKey}/filters` supplies the filter panel's fields. */
  listKey?: string;
  filterOverrides?: Record<string, ListFilterOverride>;
  /**
   * Fields the page declares itself, added after any `listKey` fields. Called
   * on every render (so it may be a hook, and `t` has initialised).
   */
  filterFields?: () => FilterField[];
  /** Rows per page; `LIST_PAGE_SIZE` when unset. */
  pageSize?: number;
  /** Filter keys rendered as a study-helper picker instead of their control. */
  helperPickerKeys?: string[];
  /** tanstack column id -> the API's sort key; named columns become toggles. */
  sortKeys?: Record<string, string>;
  defaultSort?: TableSortState;
  /**
   * Keep page/search/sort/filters in the URL. Off by default: a route whose
   * `validateSearch` strips unknown keys would silently drop them, so a route
   * opts in and passes the keys through (`readTableSearch`).
   */
  urlState?: boolean;
  /** Namespaces the URL keys when two tables share a route. */
  searchPrefix?: string;
};

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;

const Header = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: var(--component-margin);
    justify-content: space-between;
`;

const Toolbar = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: var(--component-margin);
    margin: var(--component-margin);
`;

const SearchInput = styled.input`
    border: 1px solid var(--grey-300);
    border-radius: 4px;
    font-size: 0.9rem;
    padding: 0.4rem 0.6rem;
`;

const EMPTY_FILTERS: TableFilters = {};

// Where the committed page/search/sort/filters live: the URL or component
// state. `commit` takes a partial update and resets paging unless it names a
// page, since every other change invalidates the current window.
function useTableState<D, P extends keyof FileRoutesByFullPath>(
  settings: TablePageSettings<D, P>,
): {
  state: TableSearchState;
  commit: (patch: Partial<TableSearchState>) => void;
} {
  const prefix = settings.searchPrefix ?? '';
  const urlState = settings.urlState ?? false;
  const navigate = useNavigate();
  const routeSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const [local, setLocal] = useState<TableSearchState>({
    page: 0,
    q: '',
    sort: settings.defaultSort,
    filters: EMPTY_FILTERS,
  });

  const fromUrl = readTableSearch(routeSearch, prefix);
  // An untouched URL means the default sort; a chosen one always carries its
  // key, so cycling a column "off" is a bare URL plus `sortCleared`.
  const state: TableSearchState = urlState
    ? {
        ...fromUrl,
        sort:
          fromUrl.sort ??
          (routeSearch[`${prefix}sort`] === ''
            ? undefined
            : settings.defaultSort),
      }
    : local;

  const stateRef = useRef(state);
  stateRef.current = state;

  const commit = useCallback(
    (patch: Partial<TableSearchState>) => {
      const next: TableSearchState = { ...stateRef.current, page: 0, ...patch };
      if (!urlState) {
        setLocal(next);
        return;
      }
      void navigate({
        replace: true,
        search: ((prev: Record<string, unknown>) => {
          const written = writeTableSearch(prev, next, prefix);
          // Sorting off while a default exists must survive a reload.
          if (!next.sort && settings.defaultSort) written[`${prefix}sort`] = '';
          return written;
        }) as never,
      });
    },
    [urlState, navigate, prefix, settings.defaultSort],
  );

  return { state, commit };
}

export function createTablePage<D, P extends keyof FileRoutesByFullPath>(
  settings: TablePageSettings<D, P>,
) {
  function TablePage() {
    const { t } = useTranslation();
    const name = t(settings.name);
    const { state, commit } = useTableState(settings);
    const { page, q, sort, filters } = state;

    const [searchInput, setSearchInput] = useState(q);
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Debounced commit; `q` changing from outside (back button) re-seeds the box.
    useEffect(() => setSearchInput(q), [q]);
    useEffect(() => {
      if (searchInput === q) return;
      const timer = setTimeout(
        () => commit({ q: searchInput }),
        SEARCH_DEBOUNCE_MS,
      );
      return () => clearTimeout(timer);
    }, [searchInput, q, commit]);

    const { data: filterMeta } = api.getListFilters.useQuery(
      { path: { list_key: settings.listKey ?? '' } },
      { enabled: Boolean(settings.listKey) },
    );
    const ownFilterFields = settings.filterFields?.() ?? [];
    const filterFields = [
      ...(settings.listKey
        ? listFiltersToFilterFields(
            filterMeta?.filters,
            settings.filterOverrides,
          )
        : []),
      ...ownFilterFields,
    ];
    const pageSize = settings.pageSize ?? LIST_PAGE_SIZE;

    // The panel flushes its uncontrolled inputs through onChange and calls
    // onApply in the same event, before any re-render, so the draft is a ref
    // written synchronously (as WbsTable and NetworkFilterBar do).
    const draftRef = useRef<Record<string, unknown>>(filters);
    const [panelValues, setPanelValues] =
      useState<Record<string, unknown>>(filters);
    const filtersKey = JSON.stringify(filters);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on content
    useEffect(() => {
      draftRef.current = filters;
      setPanelValues(filters);
    }, [filtersKey]);

    const trimmed = q.trim();
    const { data, isLoading, error } = settings.useData(page, trimmed, {
      paging: {
        limit: pageSize,
        offset: pageSize * page,
      },
      sort,
      filters,
    });

    const table = useReactTable({
      data: data?.data ?? [],
      columns: settings.columns,
      getCoreRowModel: getCoreRowModel(),
    });

    // none -> desc -> asc -> none, matching legacy's header glyph cycle.
    const toggleSort = (sortKey: string) => {
      if (sort?.key !== sortKey)
        commit({ sort: { key: sortKey, order: 'desc' } });
      else if (sort.order === 'desc')
        commit({ sort: { key: sortKey, order: 'asc' } });
      else commit({ sort: undefined });
    };

    const activeFilters = Object.values(filters).filter(
      (v) => !isEmptyFilter(v),
    ).length;
    const showFilters = filterFields.length > 0;

    const pagerProps = {
      page,
      pageSize,
      shown: data?.data.length ?? 0,
      total: data?.count,
      onPage: (next: number) => commit({ page: next }),
    };

    return (
      <div>
        <Header>
          <PageTitle>{name}</PageTitle>
          {settings.actions}
        </Header>
        {(settings.searchable || showFilters) && (
          <Toolbar>
            {settings.searchable && (
              <SearchInput
                type="search"
                placeholder={t(
                  settings.searchPlaceholder ?? 'admin.common.search',
                )}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            )}
            {showFilters && (
              <Button
                className={buttonStyles.Button}
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('admin.common.filters')}
                {activeFilters > 0 ? ` (${activeFilters})` : ''}
              </Button>
            )}
          </Toolbar>
        )}
        {showFilters && (
          <TableFilterPanel
            isOpen={filtersOpen}
            filters={filterFields}
            values={panelValues}
            hideRememberFilterCheckbox
            onChange={(key, value) => {
              draftRef.current = { ...draftRef.current, [key]: value };
              setPanelValues(draftRef.current);
            }}
            onApply={() => commit({ filters: pruneDraft(draftRef.current) })}
            onReset={() => {
              draftRef.current = {};
              setPanelValues({});
              commit({ filters: EMPTY_FILTERS });
            }}
            renderField={(
              field: FilterField,
              value: unknown,
              onChange: (next: string[]) => void,
            ) =>
              settings.helperPickerKeys?.includes(field.key) ? (
                <HelperFilter
                  value={Array.isArray(value) ? value.map(String) : []}
                  onChange={onChange}
                />
              ) : undefined
            }
          />
        )}
        <Pagination {...pagerProps} />

        {isLoading ? (
          <LoadingMessage>
            {t('admin.common.loadingName', {
              name,
              interpolation: { escapeValue: false },
            })}
          </LoadingMessage>
        ) : error ? (
          <ErrorMessage>
            {t('admin.common.errorLoadingName', {
              name,
              interpolation: { escapeValue: false },
            })}
          </ErrorMessage>
        ) : (
          <>
            <Table
              table={table}
              to={settings.itemBaseUrl}
              getParams={settings.getParams}
              sortKeys={settings.sortKeys}
              sort={sort}
              onSortToggle={settings.sortKeys ? toggleSort : undefined}
            />
            <Pagination {...pagerProps} hideWhenAlone />
          </>
        )}
      </div>
    );
  }

  return TablePage;
}

// The panel's draft holds anything it was handed; keep only values a query
// string can carry.
function pruneDraft(draft: Record<string, unknown>): TableFilters {
  const out: TableFilters = {};
  for (const [key, value] of Object.entries(draft)) {
    if (isEmptyFilter(value)) continue;
    if (typeof value === 'string' || typeof value === 'boolean') {
      out[key] = value;
    } else if (Array.isArray(value)) {
      out[key] = value.map(String);
    }
  }
  return out;
}
